import {
  calculateDeterministicTradeMetrics,
  type MetricBar,
  type MetricExecution,
} from './deterministicTradeMetrics';

export type TradeDetailsDeterministicSummary = {
  availability: ExcursionAvailability;
  errorReason?: string;
  grossPnl: number;
  netPnl: number;
  totalFees: number;
  bestExitGross: number;
  bestExitNet: number;
  totalMfe: number;
  totalMae: number;
  avgPositionMfe: number;
  avgPositionMae: number;
  efficiencyGrossPct: number;
  efficiencyNetPct: number;
  phaseCount: number;
  excursionPhaseCount: number;
  barExcursionPhaseCount: number;
  favorablePeakTimestampMs: number | null;
  maximumDrawdownTimestampMs: number | null;
  finalExitTimestampMs: number | null;
};

export type ExcursionAvailability =
  | 'loading'
  | 'available'
  | 'unavailable'
  | 'data_error';

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function buildTradeDetailsDeterministicSummary(input: {
  executions: MetricExecution[];
  bars: MetricBar[];
  multiplier: number;
  clampEfficiency?: boolean;
  debug?: boolean;
}): TradeDetailsDeterministicSummary {
  const result = calculateDeterministicTradeMetrics({
    executions: input.executions,
    bars: input.bars,
    multiplier: input.multiplier,
    clampEfficiency: input.clampEfficiency,
    debug: input.debug,
  });

  const phaseCount = result.phases.length;
  const excursionPhaseCount = result.phases.filter((phase) => phase.excursionPointCount > 0).length;
  const barExcursionPhaseCount = result.phases.filter((phase) => phase.excursionBarCount > 0).length;

  const grossPnl = result.phases.reduce((sum, phase) => sum + phase.actualGross, 0);
  const netPnl = result.phases.reduce((sum, phase) => sum + phase.actualNet, 0);
  const totalFees = result.phases.reduce((sum, phase) => sum + phase.totalFees, 0);
  const bestExitGross = result.phases.reduce((sum, phase) => sum + phase.bestExitGross, 0);
  const bestExitNet = result.phases.reduce((sum, phase) => sum + phase.bestExitNet, 0);
  const totalMfe = result.phases.reduce((sum, phase) => sum + phase.mfe, 0);
  const totalMae = result.phases.reduce((sum, phase) => sum + phase.mae, 0);
  const peakPhase = result.phases.reduce<(typeof result.phases)[number] | null>((best, phase) => {
    if (phase.debug.bestFavorable.time == null) return best;
    if (best == null) return phase;
    if (phase.mfe > best.mfe) return phase;
    if (phase.mfe === best.mfe && phase.startTime > best.startTime) return phase;
    return best;
  }, null);

  const efficiencyGrossPct = bestExitGross !== 0 ? (grossPnl / bestExitGross) * 100 : 0;
  const efficiencyNetPct = bestExitNet !== 0 ? (netPnl / bestExitNet) * 100 : 0;

  return {
    availability: phaseCount > 0 && excursionPhaseCount === phaseCount ? 'available' : 'unavailable',
    errorReason: phaseCount === 0
      ? 'no canonical executions'
      : excursionPhaseCount !== phaseCount
        ? 'no deterministic excursion points'
        : barExcursionPhaseCount !== phaseCount
          ? 'execution-only excursion series'
          : undefined,
    grossPnl: round2(grossPnl),
    netPnl: round2(netPnl),
    totalFees: round2(totalFees),
    bestExitGross: round2(bestExitGross),
    bestExitNet: round2(bestExitNet),
    totalMfe: round2(totalMfe),
    totalMae: round2(totalMae),
    avgPositionMfe: round2(phaseCount > 0 ? totalMfe / phaseCount : 0),
    avgPositionMae: round2(phaseCount > 0 ? totalMae / phaseCount : 0),
    efficiencyGrossPct: round2(efficiencyGrossPct),
    efficiencyNetPct: round2(efficiencyNetPct),
    phaseCount,
    excursionPhaseCount,
    barExcursionPhaseCount,
    favorablePeakTimestampMs: peakPhase?.debug.bestFavorable.time ?? null,
    maximumDrawdownTimestampMs: peakPhase?.debug.worstAdverse.time ?? null,
    finalExitTimestampMs: peakPhase?.endTime ?? null,
  };
}
